import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { arrayMove } from "@dnd-kit/sortable";
import { useEffect, useState } from "react";
import {
  ManagementField,
  ManagementFieldRequiredIn,
  ManagementFieldType,
} from "../../../../types/client.type";
import { Input } from "../../../Input";
import { Switch } from "../../../UI/Switch";
import {
  getRequiredIn,
  isExportedToClient,
  MANAGEMENT_FIELD_SLOT_COUNT,
  normalizeManagementFieldRequirement,
  requiredInLabel,
  sortManagementFields,
} from "../../../../utils/managementFields";
import { BRIEFING_STATUS_OPTIONS } from "../../../../utils/briefingStatus";

type Props = {
  fields: ManagementField[];
  onChange: (fields: ManagementField[]) => void;
  disabled?: boolean;
};

const FIELD_TYPES = [ManagementFieldType.TEXT, ManagementFieldType.DROPDOWN];

const REQUIRED_IN_OPTIONS = [
  {
    value: ManagementFieldRequiredIn.REGISTRATION,
    label: "Dados Cadastrais",
    description:
      "O campo será obrigatório no preenchimento dos dados cadastrais do projeto.",
  },
  {
    value: ManagementFieldRequiredIn.STATUS,
    label: "Status do Projeto",
    description:
      "O campo será obrigatório ao alterar o projeto para um status específico.",
  },
];

const FIELD_SLOTS = Array.from(
  { length: MANAGEMENT_FIELD_SLOT_COUNT },
  (_, index) => index + 1
);

const emptyField: ManagementField = {
  name: "",
  type: ManagementFieldType.TEXT,
  order: 0,
  export_client: true,
  required: false,
  options: [],
};

const yesNo = (value: boolean) => (value ? "Sim" : "Não");

const neighborIndex = (index: number, direction: "up" | "down") =>
  direction === "up" ? index - 1 : index + 1;

const headerCellStyle = { fontWeight: "bold" } as const;

export default function ManagementFieldsTab({
  fields,
  onChange,
  disabled,
}: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [formField, setFormField] = useState<ManagementField>({
    ...emptyField,
  });
  const [nameError, setNameError] = useState(false);
  const [orderError, setOrderError] = useState(false);
  const [optionInput, setOptionInput] = useState("");
  const [optionError, setOptionError] = useState<string | null>(null);
  const [optionsError, setOptionsError] = useState(false);
  const [statusError, setStatusError] = useState(false);

  useEffect(() => {
    if (!dialogOpen) {
      setFormField({ ...emptyField });
      setEditIndex(null);
      setNameError(false);
      setOrderError(false);
      setOptionInput("");
      setOptionError(null);
      setOptionsError(false);
      setStatusError(false);
    }
  }, [dialogOpen]);

  const sortedFields = sortManagementFields(fields);
  const isDropdown = formField.type === ManagementFieldType.DROPDOWN;
  const requiredIn = getRequiredIn(formField);
  const isRequiredByStatus = requiredIn === ManagementFieldRequiredIn.STATUS;
  const options = formField.options ?? [];
  const isSlotTaken = (slot: number) =>
    fields.some((field, index) => field.order === slot && index !== editIndex);

  const handleOpenAdd = () => {
    setFormField({ ...emptyField, options: [] });
    setDialogOpen(true);
  };

  const handleOpenEdit = (field: ManagementField) => {
    setEditIndex(fields.indexOf(field));
    setFormField({
      ...field,
      export_client: isExportedToClient(field),
      required: !!field.required,
      required_in: getRequiredIn(field),
      options: [...(field.options ?? [])],
    });
    setDialogOpen(true);
  };

  const handleDelete = (field: ManagementField) => {
    onChange(fields.filter((f) => f !== field));
  };

  const handleMove = (index: number, direction: "up" | "down") => {
    const neighbor = neighborIndex(index, direction);
    const current = sortedFields[index];
    const adjacent = sortedFields[neighbor];
    if (!current || !adjacent) return;
    onChange(
      fields.map((field) => {
        if (field === current) return { ...field, order: adjacent.order };
        if (field === adjacent) return { ...field, order: current.order };
        return field;
      })
    );
  };

  const handleAddOption = () => {
    const option = optionInput.trim();
    if (!option) {
      setOptionError("Preencha a opção antes de adicionar");
      return;
    }
    if (options.some((o) => o.trim().toLowerCase() === option.toLowerCase())) {
      setOptionError("Esta opção já está cadastrada");
      return;
    }
    setFormField({ ...formField, options: [...options, option] });
    setOptionInput("");
    setOptionError(null);
    setOptionsError(false);
  };

  const handleMoveOption = (index: number, direction: "up" | "down") => {
    setFormField({
      ...formField,
      options: arrayMove(options, index, neighborIndex(index, direction)),
    });
  };

  const handleDeleteOption = (index: number) => {
    setFormField({
      ...formField,
      options: options.filter((_, i) => i !== index),
    });
  };

  const handleSaveField = () => {
    const trimmedName = formField.name.trim();
    const duplicate = fields.some(
      (f, i) =>
        f.name.trim().toLowerCase() === trimmedName.toLowerCase() &&
        i !== editIndex
    );
    const slot = Number(formField.order);
    const invalidOrder =
      !Number.isInteger(slot) ||
      slot < 1 ||
      slot > MANAGEMENT_FIELD_SLOT_COUNT ||
      isSlotTaken(slot);
    const invalidName = !trimmedName || duplicate;
    const missingOptions = isDropdown && options.length === 0;
    const missingStatus = isRequiredByStatus && !formField.required_status;
    setOrderError(invalidOrder);
    setNameError(invalidName);
    setOptionsError(missingOptions);
    setStatusError(missingStatus);
    if (invalidOrder || invalidName || missingOptions || missingStatus) return;

    const saved: ManagementField = normalizeManagementFieldRequirement({
      ...formField,
      name: trimmedName,
      order: slot,
      options: isDropdown ? options : [],
    });

    if (editIndex !== null) {
      onChange(fields.map((f, i) => (i === editIndex ? saved : f)));
    } else {
      onChange([...fields, saved]);
    }
    setDialogOpen(false);
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} py={1}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontWeight="bold" fontSize={16}>
          Campos Gerenciais
        </Typography>
        <Button
          variant="contained"
          disabled={disabled}
          onClick={handleOpenAdd}
          style={{
            background: "#1361A4",
            color: "#FFFFFF",
            textTransform: "none",
            fontSize: 14,
          }}
        >
          Adicionar Campo Gerencial
        </Button>
      </Box>

      {sortedFields.length === 0 ? (
        <Box p={4} textAlign="center" border="1px dashed #ccc" borderRadius={2}>
          <Typography color="textSecondary">
            Nenhum campo gerencial cadastrado.
          </Typography>
        </Box>
      ) : (
        <TableContainer
          style={{
            boxShadow: "0px 0px 2px 1px #C4C4C4",
            borderRadius: 4,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ ...headerCellStyle, width: 150 }}>
                  Campo Gerencial
                </TableCell>
                <TableCell style={headerCellStyle}>Nome</TableCell>
                <TableCell style={headerCellStyle}>Tipo</TableCell>
                <TableCell style={headerCellStyle}>Obrigatório</TableCell>
                <TableCell style={headerCellStyle}>Obrigatório em</TableCell>
                <TableCell style={headerCellStyle}>Export Cliente?</TableCell>
                <TableCell style={{ ...headerCellStyle, width: 140 }}>
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedFields.map((field, index) => (
                <TableRow key={`${field.order}-${field.name}`}>
                  <TableCell>{field.order}</TableCell>
                  <TableCell>{field.name}</TableCell>
                  <TableCell>
                    {field.type || ManagementFieldType.TEXT}
                  </TableCell>
                  <TableCell>{yesNo(!!field.required)}</TableCell>
                  <TableCell>{requiredInLabel(field)}</TableCell>
                  <TableCell>{yesNo(isExportedToClient(field))}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="Mover para cima">
                        <span>
                          <IconButton
                            size="small"
                            disabled={index === 0 || disabled}
                            onClick={() => handleMove(index, "up")}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Mover para baixo">
                        <span>
                          <IconButton
                            size="small"
                            disabled={
                              index === sortedFields.length - 1 || disabled
                            }
                            onClick={() => handleMove(index, "down")}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <span>
                          <IconButton
                            size="small"
                            disabled={disabled}
                            onClick={() => handleOpenEdit(field)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <span>
                          <IconButton
                            size="small"
                            disabled={disabled}
                            onClick={() => handleDelete(field)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editIndex !== null
            ? "Editar Campo Gerencial"
            : "Adicionar Campo Gerencial"}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Input
              select
              marginTop={0}
              size="small"
              label="Campo Gerencial"
              value={
                formField.order >= 1 &&
                formField.order <= MANAGEMENT_FIELD_SLOT_COUNT
                  ? formField.order
                  : ""
              }
              onChange={(e) => {
                setFormField({ ...formField, order: Number(e.target.value) });
                if (orderError) setOrderError(false);
              }}
              error={orderError}
              helperText={
                orderError
                  ? isSlotTaken(Number(formField.order))
                    ? "Este Campo Gerencial já está em uso"
                    : "Selecione o Campo Gerencial"
                  : undefined
              }
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) =>
                  selected === "" || selected == null ? (
                    <span style={{ color: "#9e9e9e" }}>Selecione</span>
                  ) : (
                    String(selected)
                  ),
              }}
            >
              <MenuItem value="" sx={{ display: "none" }}>
                Selecione
              </MenuItem>
              {FIELD_SLOTS.map((slot) => (
                <MenuItem key={slot} value={slot} disabled={isSlotTaken(slot)}>
                  {slot}
                </MenuItem>
              ))}
            </Input>
            <Input
              label="Nome do Campo"
              value={formField.name}
              onChange={(e) => {
                setFormField({ ...formField, name: e.target.value });
                if (nameError) setNameError(false);
              }}
              error={nameError}
              helperText={
                nameError ? "Nome é obrigatório e deve ser único" : undefined
              }
              placeholder="Digite o nome do campo"
            />
            <Input
              select
              marginTop={0}
              size="small"
              label="Tipo"
              value={formField.type}
              onChange={(e) =>
                setFormField({ ...formField, type: e.target.value })
              }
            >
              {FIELD_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Input>
            <Box display="flex">
              <Box flex={1}>
                <Switch
                  label="Export Cliente?"
                  valueLabel={yesNo(isExportedToClient(formField))}
                  checked={isExportedToClient(formField)}
                  onChange={(e) =>
                    setFormField({
                      ...formField,
                      export_client: e.target.checked,
                    })
                  }
                />
              </Box>
              <Box flex={1}>
                <Switch
                  label="Obrigatório?"
                  valueLabel={yesNo(!!formField.required)}
                  checked={!!formField.required}
                  onChange={(e) => {
                    setFormField({
                      ...formField,
                      required: e.target.checked,
                      required_in:
                        formField.required_in ??
                        ManagementFieldRequiredIn.REGISTRATION,
                    });
                    setStatusError(false);
                  }}
                />
              </Box>
            </Box>

            {requiredIn && (
              <FormControl>
                <FormLabel
                  required
                  sx={{ fontWeight: "bold", fontSize: 14, color: "inherit" }}
                >
                  Obrigatório em
                </FormLabel>
                <RadioGroup
                  value={requiredIn}
                  onChange={(e) => {
                    setFormField({
                      ...formField,
                      required_in: e.target.value as ManagementFieldRequiredIn,
                    });
                    setStatusError(false);
                  }}
                >
                  {REQUIRED_IN_OPTIONS.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio size="small" />}
                      sx={{ alignItems: "flex-start", mt: 1 }}
                      label={
                        <Box>
                          <Typography fontSize={14}>{option.label}</Typography>
                          <Typography fontSize={12} color="textSecondary">
                            {option.description}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}

            {isRequiredByStatus && (
              <Input
                select
                marginTop={0}
                size="small"
                label="Status *"
                value={formField.required_status ?? ""}
                onChange={(e) => {
                  setFormField({
                    ...formField,
                    required_status: e.target.value,
                  });
                  setStatusError(false);
                }}
                error={statusError}
                helperText={statusError ? "Status é obrigatório" : undefined}
              >
                {BRIEFING_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.text}
                  </MenuItem>
                ))}
              </Input>
            )}

            {isDropdown && (
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography fontWeight="bold" fontSize={14}>
                  Itens do Dropdown
                </Typography>
                <Box display="flex" gap={1} alignItems="flex-start">
                  <Input
                    marginTop={0}
                    size="small"
                    value={optionInput}
                    placeholder="Digite uma opção"
                    onChange={(e) => {
                      setOptionInput(e.target.value);
                      if (optionError) setOptionError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddOption();
                      }
                    }}
                    error={!!optionError}
                    helperText={optionError ?? undefined}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddOption}
                    style={{
                      background: "#1361A4",
                      textTransform: "none",
                      height: 40,
                      minWidth: 110,
                    }}
                  >
                    Adicionar
                  </Button>
                </Box>

                {options.length > 0 && (
                  <TableContainer
                    style={{
                      boxShadow: "0px 0px 2px 1px #C4C4C4",
                      borderRadius: 4,
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ ...headerCellStyle, width: 70 }}>
                            Ordem
                          </TableCell>
                          <TableCell style={headerCellStyle}>Opção</TableCell>
                          <TableCell style={{ ...headerCellStyle, width: 120 }}>
                            Ações
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {options.map((option, index) => (
                          <TableRow key={option}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{option}</TableCell>
                            <TableCell>
                              <Box display="flex" gap={0.5}>
                                <Tooltip title="Mover para cima">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={index === 0}
                                      onClick={() =>
                                        handleMoveOption(index, "up")
                                      }
                                    >
                                      <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Mover para baixo">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={index === options.length - 1}
                                      onClick={() =>
                                        handleMoveOption(index, "down")
                                      }
                                    >
                                      <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Excluir">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteOption(index)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {optionsError && (
                  <FormHelperText error sx={{ m: 0 }}>
                    Cadastre ao menos uma opção para salvar
                  </FormHelperText>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setDialogOpen(false)}
            style={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveField}
            style={{
              background: "#1361A4",
              textTransform: "none",
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
